import { useCallback, useEffect, useState } from 'react'
import { Button, Input, Popover, Typography } from '@musinsa/pbo-core'

/** 다건 입력 최대 개수 — 공용 `MultipleInputV2` 와 동일 상한. */
const MAX_SIZE = 500

const PLACEHOLDER = '다건 입력 시, 우측 + 버튼을 눌러주세요.'

/** 콤마·개행·탭으로 끊어 공백 제거 후 중복을 없앤다. 상한을 적용하지 않은 원본 개수다. */
const split = (text: string): string[] => [
  ...new Set(
    text
      .split(/[,\n\t]/)
      .map((v) => v.trim())
      .filter(Boolean),
  ),
]

/** 폼에 넘길 값 — [split] 결과를 상한(500건)까지 자른다. */
const parse = (text: string): string[] => split(text).slice(0, MAX_SIZE)

interface Props {
  value?: string[]
  onChange?: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
}

/**
 * 다건 입력 필드 (브랜드명·바코드명·스타일번호).
 *
 * - **입력창에 직접 타이핑**할 수 있다. 콤마로 끊으면 여러 건이 된다.
 * - 우측 `+` 버튼은 여러 줄 붙여넣기용 패널을 연다(엑셀에서 복사한 목록 등).
 *
 * 공용 `MultipleInputV2` 를 쓰지 않는 이유 — 그 컴포넌트는 `PopoverInput.Trigger` 라
 * 트리거가 표시 전용이고 직접 타이핑이 불가능하다.
 */
export function MultiValueInput({ value, onChange, placeholder = PLACEHOLDER, disabled }: Props) {
  const committed = (value ?? []).join(', ')
  const [text, setText] = useState(committed)
  const [isOpen, setIsOpen] = useState(false)
  const [bulkText, setBulkText] = useState('')

  // 외부에서 값이 바뀌면(초기화·서치헬퍼 등) 입력창 문구를 맞춘다.
  // 타이핑 중에는 파싱 결과가 같으므로 커서가 튀지 않는다.
  useEffect(() => {
    setText((prev) => (parse(prev).join(', ') === committed ? prev : committed))
  }, [committed])

  const handleTextChange = useCallback(
    (next: string) => {
      setText(next)
      onChange?.(parse(next))
    },
    [onChange],
  )

  const handleBulkOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (open) setBulkText((value ?? []).join('\n'))
    },
    [value],
  )

  const handleBulkApply = useCallback(() => {
    const next = parse(bulkText)
    setText(next.join(', '))
    onChange?.(next)
    setIsOpen(false)
  }, [bulkText, onChange])

  const count = parse(text).length
  const bulkCount = split(bulkText).length

  return (
    <Input
      value={text}
      onChange={(e) => handleTextChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      allowClear
      suffix={
        <Popover
          open={isOpen}
          onOpenChange={handleBulkOpen}
          trigger="click"
          placement="bottomRight"
          title="다건 입력"
          content={
            <div style={{ width: 280 }}>
              <Input.TextArea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={'한 줄에 하나씩 입력하세요.\n엑셀에서 복사해 붙여넣을 수 있습니다.'}
                autoSize={{ minRows: 5, maxRows: 12 }}
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 8 }}>
                {/* 상한 초과 여부는 자르기 전 개수로 판단한다 — parse() 결과로 보면 500 을 넘을 수 없어 경고가 죽는다. */}
                <Typography.Text type={bulkCount > MAX_SIZE ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                  {bulkCount.toLocaleString()} / {MAX_SIZE.toLocaleString()}
                  {bulkCount > MAX_SIZE && ` — 앞 ${MAX_SIZE.toLocaleString()}건만 적용됩니다.`}
                </Typography.Text>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" onClick={() => setBulkText('')}>
                    초기화
                  </Button>
                  <Button size="small" type="primary" onClick={handleBulkApply}>
                    적용
                  </Button>
                </div>
              </div>
            </div>
          }
        >
          <Button
            type="text"
            size="small"
            disabled={disabled}
            aria-label="다건 입력"
            style={{ padding: '0 4px', minWidth: 'auto', height: 20, lineHeight: '20px' }}
          >
            +{count > 1 ? ` ${count}` : ''}
          </Button>
        </Popover>
      }
    />
  )
}
